import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MaterialReactTable,
  useMaterialReactTable,
  createMRTColumnHelper,
  type MRT_ColumnDef,
} from 'material-react-table';

import {
  Box,
  Button,
  ListItemIcon,
  MenuItem,
  Typography,
  lighten,
} from '@mui/material';

import { LoanAmortization } from '../../../interface/ILoanManagement';

import helper from '../../../helpers/index';

import iconView from '../../../assets/visible.png';
import iconEdit from '../../../assets/edit-icon.png';
import iconDelete from '../../../assets/delete.png';



//example data type


const columnHelper = createMRTColumnHelper<LoanAmortization>();

interface LoanAmortizationTableProps {
  data: LoanAmortization[];
  updatePaymentStatus: (loanAmortizationId: number, isPaid: boolean) => void;
}



const LoanAmortizationTable: React.FC<LoanAmortizationTableProps> = ({ data, updatePaymentStatus }) => {


  //should be memoized or stable
  const columns = useMemo<MRT_ColumnDef<LoanAmortization>[]>(
    () => [
      {
        accessorKey: 'loanTermAmortizationId',
        header: 'Payment No.',
        enableSorting: true
      },
      {
        accessorFn: (row: LoanAmortization) => row.dueDate ? `${helper.formatLongDate(row.dueDate)}` : '',
        id: 'dueDate',
        header: 'Due Date',
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const firstData = new Date(rowA.original.dueDate || new Date()).getTime() || 0;
          const secondData = new Date(rowB.original.dueDate || new Date()).getTime() || 0;
          // Perform numeric comparison
          return Number(firstData) - Number(secondData);
        }
      },
      {
        // accessorKey: 'periodicPayments',
        accessorFn: function(row: LoanAmortization){
          return `${helper.formatNumber(row.periodicPayments)}`
        },
        header: 'Periodic Payments',
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.periodicPayments;
          const idB = rowB.original.periodicPayments;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        accessorFn: function(row: LoanAmortization)
        {
          return `${helper.formatNumber(row.principalRepaid)}`
        },
        // accessorKey: 'principalRepaid',
        header: 'Principal Repaid',
        size: 200,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.principalRepaid;
          const idB = rowB.original.principalRepaid;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        // accessorKey: 'interestPaid',
        accessorFn: function(row: LoanAmortization)
        {
          return `${helper.formatNumber(row.interestPaid)}`
        },
        header: 'Intereset Paid',
        size: 200,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.interestPaid;
          const idB = rowB.original.interestPaid;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        // accessorKey: 'unpaidBalance',
        accessorFn: function(row: LoanAmortization)
        {
          return `${helper.formatNumber(row.unpaidBalance)}`
        },
        header: 'Unpaid Balance',
        size: 200,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.unpaidBalance;
          const idB = rowB.original.unpaidBalance;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        header: 'Status',
        accessorFn: (row: LoanAmortization) => {
          // Initialize local state for each row's payment status

          return (
            <Box>
              <select
                style={{ width: '100%' }}
                value={String(row.isPaid)} // Set the select value dynamically
                onChange={async (e) => {
                  const isPaid = e.target.value === 'true'; // Convert string to boolean
                  updatePaymentStatus(
                    Number(row.loanTermAmortizationId),
                    isPaid
                  );

                }}
              >
                <option value="false">Unpaid</option>
                <option value="true">Paid</option>
              </select>
            </Box>
          );
        }
      }
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data,
    muiTablePaperProps: {
      elevation: 1,
      sx: {
        boxShadow: 'none',
        width: '100%',
        background: 'transparent',
        display: 'grid',

      }
    },
    enableColumnActions: false,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.index % 2 === 0 ? 'transparent' : '#E7EFEA',
      },
    }),

  });

  return <MaterialReactTable table={table} />;
};

export default LoanAmortizationTable;
